import { useState } from 'react'
import { Search, Filter, Calendar, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { FilterState } from '@/services/mockLeads'

interface LeadFiltersProps {
  filters: FilterState
  onFiltersChange: (filters: FilterState) => void
  totalLeads: number
  filteredLeads: number
  onExport: () => void
}

export function LeadFilters({ 
  filters, 
  onFiltersChange, 
  totalLeads, 
  filteredLeads,
  onExport 
}: LeadFiltersProps) {
  const [showAdvanced, setShowAdvanced] = useState(false)
  
  const activeFilterCount = Object.entries(filters).filter(([key, value]) => {
    if (key === 'search') return value.length > 0
    return value !== 'all' && value !== 'today'
  }).length
  
  const updateFilter = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    onFiltersChange({ ...filters, [key]: value })
  }
  
  return (
    <div className="space-y-4 bg-card border-b p-4">
      {/* Header Row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold">Lead Review</h2>
          <Badge variant="secondary">
            {filteredLeads} of {totalLeads} leads
          </Badge>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          
          <Popover open={showAdvanced} onOpenChange={setShowAdvanced}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                Filters
                {activeFilterCount > 0 && (
                  <Badge className="ml-2 h-5 w-5 p-0 text-xs">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            
            <PopoverContent className="w-96 p-4" align="end">
              <div className="space-y-4">
                <div className="font-medium">Advanced Filters</div>
                
                {/* Date Range */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Date Range</label>
                  <Select 
                    value={filters.dateRange} 
                    onValueChange={(value) => updateFilter('dateRange', value as any)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="today">Today</SelectItem>
                      <SelectItem value="yesterday">Yesterday</SelectItem>
                      <SelectItem value="week">Last 7 days</SelectItem>
                      <SelectItem value="month">Last 30 days</SelectItem>
                      <SelectItem value="custom">Custom Range</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Risk Level */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Risk Level</label>
                  <Select 
                    value={filters.riskLevel} 
                    onValueChange={(value) => updateFilter('riskLevel', value as any)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Risk Levels</SelectItem>
                      <SelectItem value="low">Low (0-30)</SelectItem>
                      <SelectItem value="medium">Medium (31-60)</SelectItem>
                      <SelectItem value="high">High (61-80)</SelectItem>
                      <SelectItem value="critical">Critical (81-100)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Status */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Status</label>
                  <Select 
                    value={filters.status} 
                    onValueChange={(value) => updateFilter('status', value as any)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending Review</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Source */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Source</label>
                  <Select 
                    value={filters.source} 
                    onValueChange={(value) => updateFilter('source', value as any)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sources</SelectItem>
                      <SelectItem value="everflow">Everflow</SelectItem>
                      <SelectItem value="fluent">Fluent Forms</SelectItem>
                      <SelectItem value="manual">Manual Entry</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Pattern Type */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Pattern Type</label>
                  <Select 
                    value={filters.pattern} 
                    onValueChange={(value) => updateFilter('pattern', value as any)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Patterns</SelectItem>
                      <SelectItem value="sequential">Sequential Phone</SelectItem>
                      <SelectItem value="velocity">Velocity Attack</SelectItem>
                      <SelectItem value="ip_cluster">IP Cluster</SelectItem>
                      <SelectItem value="domain">Suspicious Domain</SelectItem>
                      <SelectItem value="zip">Zip Concentration</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex justify-between pt-4 border-t">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => onFiltersChange({
                      dateRange: 'today',
                      riskLevel: 'all',
                      status: 'all',
                      source: 'all',
                      pattern: 'all',
                      search: ''
                    })}
                  >
                    Clear All
                  </Button>
                  
                  <Button size="sm" onClick={() => setShowAdvanced(false)}>
                    Apply Filters
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      
      {/* Search Bar */}
      <div className="flex items-center gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by email, phone, IP, name, or ID..."
            value={filters.search}
            onChange={(e) => updateFilter('search', e.target.value)}
            className="pl-9"
          />
        </div>
        
        {/* Quick Filter Pills */}
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Quick:</span>
          
          <Button
            variant={filters.riskLevel === 'critical' ? 'default' : 'outline'}
            size="sm"
            onClick={() => updateFilter('riskLevel', 
              filters.riskLevel === 'critical' ? 'all' : 'critical'
            )}
          >
            Critical Risk
          </Button>
          
          <Button
            variant={filters.status === 'pending' ? 'default' : 'outline'}
            size="sm"
            onClick={() => updateFilter('status', 
              filters.status === 'pending' ? 'all' : 'pending'
            )}
          >
            Pending
          </Button>
          
          <Button
            variant={filters.pattern === 'sequential' ? 'default' : 'outline'}
            size="sm"
            onClick={() => updateFilter('pattern', 
              filters.pattern === 'sequential' ? 'all' : 'sequential'
            )}
          >
            Sequential
          </Button>
        </div>
      </div>
      
      {/* Active Filters */}
      {activeFilterCount > 0 && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Active filters:</span>
          <div className="flex flex-wrap gap-1">
            {filters.dateRange !== 'today' && (
              <Badge variant="secondary" className="text-xs">
                Date: {filters.dateRange}
              </Badge>
            )}
            {filters.riskLevel !== 'all' && (
              <Badge variant="secondary" className="text-xs">
                Risk: {filters.riskLevel}
              </Badge>
            )}
            {filters.status !== 'all' && (
              <Badge variant="secondary" className="text-xs">
                Status: {filters.status}
              </Badge>
            )}
            {filters.source !== 'all' && (
              <Badge variant="secondary" className="text-xs">
                Source: {filters.source}
              </Badge>
            )}
            {filters.pattern !== 'all' && (
              <Badge variant="secondary" className="text-xs">
                Pattern: {filters.pattern}
              </Badge>
            )}
            {filters.search && (
              <Badge variant="secondary" className="text-xs">
                Search: "{filters.search}"
              </Badge>
            )}
          </div>
        </div>
      )}
    </div>
  )
}